import React, { useState, useEffect } from 'react';
import ReviewModal from './TripAdvisorReviewModalComponent';

interface Location {
  location_id: number;
  name: string;
}

interface ApiResponse {
  data: Location[];
  message?: string;
}

interface TripAdvisorRatingProps {
  hotelName?: string;
}

interface Subrating {
  name: string;
  localized_name: string;
  rating_image_url: string;
  value: string;
}

interface ReviewRatingCount {
  [key: string]: string;
}

const TripAdvisorRating: React.FC<TripAdvisorRatingProps> = ({ hotelName }) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationDetails, setLocationDetails] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [subratings, setSubratings] = useState<Subrating[]>([]);
  const [reviewCounts, setReviewCounts] = useState<ReviewRatingCount>({});
  //const ratingText = ['Terrible', 'Poor', 'Average', 'Very good', 'Excellent'];
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (hotelName) {
      handleSearch(hotelName);
    }
  }, [hotelName]);

  const handleSearch = async (query: string) => {
    setError(null);
    try {
      const response = await fetch(`https://www.haloholidays.co.uk/api/tripadvisor?searchQuery=${encodeURIComponent(query)}&language=en`, {
        headers: {
          'Accept': 'application/json',

        },
        // credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status} - ${response.statusText}`);
      }

      const data: ApiResponse = await response.json();
      if (data.data && data.data.length > 0) {
        setLocations([data.data[0]]);
        handleFetchLocationDetails(data.data[0].location_id.toString(), 'en', 'USD');
      } else {
        setError('No locations found or data is in unexpected format');
      }
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError(error.message || 'An unexpected error occurred');
    }
  };

  const handleFetchLocationDetails = async (locationId: string, language: string, currency: string) => {
    setError(null);
    try {
      const response = await fetch(`https://www.haloholidays.co.uk/api/tripadvisor/details?locationId=${encodeURIComponent(locationId)}&language=${encodeURIComponent(language)}&currency=${encodeURIComponent(currency)}`, {
        headers: {
          'Accept': 'application/json',

        },
        // credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      if (data) {
        setLocationDetails(data);
        const subratingsArray: Subrating[] = Object.values(data.subratings || {});
        setSubratings(subratingsArray);
        setReviewCounts(data.review_rating_count || {});
      } else {
        setError('Data is in unexpected format');
      }
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError(error.message || 'An unexpected error occurred');
    }
  };

  // const scrollToReviews = () => {
  //   const reviewsSection = document.getElementById('tripAdvisorReview');
  //   if (reviewsSection) {
  //     const offset = 6 * 18;
  //     const reviewsSectionTop = reviewsSection.getBoundingClientRect().top + window.pageYOffset - offset;

  //     window.scrollTo({
  //       top: reviewsSectionTop,
  //       behavior: 'smooth'
  //     });
  //   }
  // };

  const openModal = () => {
    setShowModal(true);
  };


  return (
    <div className="trip-advisor-bg p-4 bg-white rounded-xl h-fit border border-gray-200">
      <div className="">
        <header className="flex justify-center items-center mb-4">
          <img
            src="/TripAdvisor/trip_advisor.webp"
            alt="Tripadvisor logo"
            className="w-[50%] md:w-[50%]"
            width="210px" // Set the appropriate width
            height="45px" // Set the appropriate height
          />
        </header>
        {locationDetails && locations && !error ? (
          <div>
            <div className="flex items-center justify-center"> {/* Adjusted this line */}
              <div className="flex items-center">
                <img
                  src={locationDetails.rating_image_url}
                  className="h-5"
                  alt={`${locationDetails.rating} of 5`}
                  width="100%" // Set the explicit width
                  height="20" // Set the explicit height
                  loading="lazy"
                />
                <span className="text-md font-bold">{locationDetails.rating}/5</span>
              </div>
              <div className="text-xs text-gray-600 ml-2">({locationDetails.num_reviews} reviews)</div>
            </div>

            {/* <ul className='space-y-1 mt-2'>
              {Object.entries(reviewCounts).reverse().map(([rating, count], index) => {
                const numericCount = parseFloat(count);
                const totalReviewCount = locationDetails.num_reviews;
                let adjustedReviewCount;
                if (totalReviewCount >1000) {
                  adjustedReviewCount = isNaN(numericCount) ? 0 : (numericCount / 100).toFixed(0);
                } else {
                  adjustedReviewCount = isNaN(numericCount) ? 0 : (numericCount / 10).toFixed(0);
                }

                return (
                  <li key={index}>
                    <div className='flex justify-between items-center'>
                      <div className='text-sm ml-1 text-gray-700'>{ratingText[parseInt(rating) - 1]}</div>
                      <div className="progress-container">
                        <div className="progress-bar" style={{ width: `${adjustedReviewCount}%` }}></div>
                      </div>
                    </div>
                    
                  </li>
                );
              })}
            </ul> */}

            <ul className="space-y-1 mt-2 hidden md:block lg:block lgx2:block">
              {subratings.map((subrating, index) => (
                <li key={index} className="score flex justify-between items-center ml-1 mt-2">
                  <div className="label-container">
                    <span className="text-gray-700 text-sm">{subrating.localized_name}</span>
                  </div>
                  <div className="flex items-center mr-1">
                    <img
                      src={subrating.rating_image_url}
                      className="h-3"
                      alt={`${subrating.value} of 5`}
                      width="100%" // Set the explicit width
                      height="12" // Set the explicit height
                      loading="lazy"
                    />
                    <span className="ml-2 text-gray-700 text-sm">{subrating.value}</span>
                  </div>
                </li>
              ))}
            </ul>
            <p className="anchor-link mt-4 text-center">
              <a className="link text-blue-500 hover:underline cursor-pointer" onClick={openModal}>View reviews</a>
            </p>
          </div>
        ) : (
          <div>Loading...</div>
        )}
      </div>
      {showModal && <ReviewModal locationDetails={locationDetails} location={locations} subratings={subratings} reviewCounts={reviewCounts} closeModal={() => setShowModal(false)} />}
    </div>
  );
};

export default TripAdvisorRating;
